import { useState } from "react";
import { motion } from "framer-motion";
import { FileText, DownloadSimple, LockSimple, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Reveal, Eyebrow } from "./Reveal";
import { TEMPLATES } from "../data/templates";
import { api, formatApiError } from "../lib/api";

export default function Templates() {
  const [email, setEmail] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [sending, setSending] = useState(false);

  const unlock = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/template-downloads", { email, template: "unlock-all" });
      setUnlocked(true);
      toast.success("Templates unlocked — download away.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const download = async (t) => {
    try {
      await api.post("/template-downloads", { email, template: t.id });
    } catch {
      // logging is best-effort
    }
    const blob = new Blob([t.body], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${t.id}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <section id="templates" data-testid="templates-section" className="py-24 md:py-36 border-t border-border">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <Reveal className="max-w-3xl">
          <Eyebrow>Free Letter Templates</Eyebrow>
          <h2 className="font-serif text-primary tracking-tight text-4xl sm:text-5xl mt-6 leading-[1.08]" data-testid="templates-heading">
            Words that get <em className="text-accent">answers</em>
          </h2>
          <p className="mt-6 text-foreground/70 max-w-xl">
            Three field-tested letters — the deed-back request, the cold-call filter, and the charge
            dispute. Fill in the brackets, send them off, keep the replies.
          </p>
        </Reveal>

        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {TEMPLATES.map((t, i) => (
            <Reveal key={t.id} delay={i * 0.1} className="h-full">
              <div className="h-full bg-card border border-border rounded-sm p-8 flex flex-col hover:border-accent/60 hover:-translate-y-1 transition-[border-color,transform] duration-500" data-testid={`template-card-${t.id}`}>
                <FileText size={28} weight="duotone" className="text-accent" />
                <h3 className="font-serif text-2xl text-primary tracking-tight mt-5">{t.title}</h3>
                <p className="mt-3 text-sm text-foreground/65 leading-relaxed flex-1">{t.purpose}</p>
                {unlocked ? (
                  <button
                    data-testid={`template-download-${t.id}`}
                    onClick={() => download(t)}
                    className="group mt-7 flex items-center justify-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full font-semibold text-sm hover:bg-accent transition-colors duration-300"
                  >
                    <DownloadSimple size={15} weight="bold" /> Download letter
                  </button>
                ) : (
                  <div className="mt-7 flex items-center justify-center gap-2 border border-dashed border-border rounded-full px-6 py-3 text-sm font-semibold text-muted-foreground" data-testid={`template-locked-${t.id}`}>
                    <LockSimple size={15} weight="bold" /> Unlock with your email
                  </div>
                )}
              </div>
            </Reveal>
          ))}
        </div>

        {!unlocked ? (
          <Reveal delay={0.2}>
            <form onSubmit={unlock} className="mt-10 flex flex-col sm:flex-row gap-3 max-w-lg" data-testid="templates-unlock-form">
              <input
                data-testid="templates-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                className="flex-1 bg-card border border-border rounded-sm px-4 py-3.5 text-sm font-medium text-foreground placeholder:text-foreground/35 focus:outline-none focus:border-primary transition-colors duration-300"
              />
              <button
                data-testid="templates-unlock-button"
                type="submit"
                disabled={sending}
                className="flex items-center justify-center gap-2 bg-primary text-primary-foreground px-7 py-3.5 rounded-full font-semibold text-sm hover:bg-accent disabled:opacity-50 transition-colors duration-300 shrink-0"
              >
                {sending ? "Unlocking…" : "Unlock all three"}
              </button>
            </form>
          </Reveal>
        ) : (
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-10 flex items-center gap-3 text-sm font-semibold text-primary"
            data-testid="templates-unlocked-note"
          >
            <CheckCircle size={20} weight="fill" /> All three letters are yours.
          </motion.p>
        )}
        <p className="mt-4 text-xs text-foreground/45 max-w-lg">
          One useful email when a new template or Red Flag Friday drops. No noise, unsubscribe anytime.
        </p>
      </div>
    </section>
  );
}
